/** Localise org role slugs to display labels.
 *
 * The backend seeds every org with SYSTEM roles whose stored `name` is the
 * English default ("Organization admin" / "Inspector / Operator" / "Observer"
 * / "Auditor"). Those defaults get a translated label via `roles.slug.<slug>`.
 * Org-defined CUSTOM roles have no per-org translation, so they fall back to
 * their backend `name` (an admin-chosen label) or, absent that, a prettified
 * slug.
 *
 * Single source of truth so MemberList, InviteMemberModal and the RoleEditor
 * all render role names the same way (they used to diverge — the member list
 * localised, the invite chips + roles page showed raw English). */
import type { TFunction } from "i18next";

/** System role slugs seeded for every org (see accounts.permissions_registry). */
const SYSTEM_SLUGS = new Set([
  "owner",
  "org_admin",
  "inspector",
  "member",
  "observer",
  "auditor",
]);

function prettifySlug(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1).replace(/_/g, " ");
}

/** Localised label for a single role slug. Pass the backend `name` as
 * `fallbackName` so custom roles keep their admin-chosen label. */
export function roleLabel(t: TFunction, slug: string, fallbackName?: string): string {
  if (SYSTEM_SLUGS.has(slug)) return t(`roles.slug.${slug}`);
  return fallbackName?.trim() || prettifySlug(slug);
}

/** Comma-joined labels for a list of slugs (no Role objects to hand). */
export function roleLabels(t: TFunction, slugs: string[]): string {
  return slugs.map((s) => roleLabel(t, s)).join(", ");
}
